import { memo, useRef } from "react";
import clsx from "clsx";

import mainClasses from "../styles.module.scss";
import {
  FormInputBox,
  InnerInput,
  InputBGIcon,
  LoadingInputIcon,
} from "../formControl";

import type { ISelectProps } from "./types";

import { useReadOnly } from "@/hooks";
import {
  InputMenuItemPopover,
  InputPopover,
  InputPopoverContent,
  useInputPopoverContext,
} from "@/components/organisms/inputPopover";
import { Icon } from "@/components/atoms/icons";
import { EmptyOption } from "@/interfaces";

export const Select = memo(function Select(props: ISelectProps) {
  const { disabled: propsDisabled, readOnly: propsReadOnly, ...rest } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const disabled = propsDisabled || rest.isLoading;

  return (
    <InputPopover ariaRole={"select"} disabled={disabled || readOnly}>
      <SelectInput {...rest} disabled={disabled} readOnly={readOnly} />
    </InputPopover>
  );
});

function SelectInput(props: ISelectProps) {
  const {
    readOnly,
    disabled,
    value = { key: "", value: "" },
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
    options,
    validation,
    isLoading,
    isError,
    ...rest
  } = props;

  const {
    activeIndex,
    setOpen,
    refs,
    open: isOpen,
    getReferenceProps,
  } = useInputPopoverContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const focusOnInput = () => inputRef.current?.focus();

  const handleSelect = () => {
    if (activeIndex === null) return;
    const activeItem = options[activeIndex];

    onChange?.(activeItem || EmptyOption);
    setOpen(false);
  };

  return (
    <>
      <FormInputBox
        ref={refs.setReference}
        disabled={Boolean(disabled)}
        readOnly={Boolean(readOnly)}
        validation={validation}
        onMouseDown={(event) => {
          event.preventDefault();
          focusOnInput();
          setOpen(!isOpen);
        }}
        style={{ cursor: "default" }}
      >
        <div className={mainClasses.inputContainer}>
          <InnerInput
            {...getReferenceProps({
              style: { cursor: "default" },
              "aria-autocomplete": "list",
              onKeyDown(event) {
                if (event.key !== "Enter") return;
                // prevent form submitting
                event.preventDefault();
                handleSelect();
              },
            })}
            className={clsx([mainClasses.input, "w-100"])}
            readOnly
            disabled={disabled}
            ref={inputRef}
            value={value.value}
            autoComplete={"off"}
            onFocus={(event) => {
              setOpen(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              setOpen(false);
              onBlur?.(event);
            }}
            onMouseDown={(event) => {
              event.stopPropagation();
              // to disable input value selection
              event.preventDefault();
              focusOnInput();
              setOpen(!isOpen);
              onMouseDown?.(event);
            }}
            {...rest}
          />
        </div>
        <LoadingInputIcon isError={isError} isLoading={isLoading}>
          <InputBGIcon disabled={disabled} readOnly={readOnly}>
            <Icon name={"AltArrowDown"} />
          </InputBGIcon>
        </LoadingInputIcon>
      </FormInputBox>
      <InputPopoverContent>
        {options.map((item, index) => {
          return (
            <InputMenuItemPopover
              key={item.key}
              index={index}
              onClick={handleSelect}
              onMouseDown={
                // cause we don't want to lose input's focus on icon clicking event
                (event) => {
                  event.preventDefault();
                }
              }
            >
              {item.value}
            </InputMenuItemPopover>
          );
        })}
      </InputPopoverContent>
    </>
  );
}
